import { ServiceProviderViewModel } from 'functions/app/service-provider/view'
import { useLanguage } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import { useMemo } from 'react'
import { addressBuilder } from '@app/util/helpers'
import { TextLink } from '@app/components/core/link/TextLink'
import Row from '@app/components/layout/Row'
import { Tag } from '@app/components/core/tag/Tag'
import { DetailsPanel } from '@app/components/blocks/detailsPanel/DetailsPanel'

export const Contact = ({
  serviceProvider,
}: {
  serviceProvider: ServiceProviderViewModel
}) => {
  const [language] = useLanguage()
  const translations = i18n.languages[language].app.serviceProviderPage

  const detailPanelItems = useMemo(() => {
    const items = []

    if (serviceProvider.location?.address) {
      items.push({
        label: translations.address,
        value: addressBuilder(serviceProvider.location.address),
      })
    }

    if (serviceProvider.contact?.phone) {
      items.push({
        label: translations.phone,
        value: (
          <TextLink
            to={`tel:${serviceProvider.contact.phone}`}
            target="_blank"
            externalURL
          >
            {serviceProvider.contact.phone}
          </TextLink>
        ),
      })
    }

    if (serviceProvider.contact?.website) {
      items.push({
        label: translations.website,
        value: (
          <TextLink
            to={serviceProvider.contact.website}
            target="_blank"
            externalURL
          >
            {serviceProvider.contact.website}
          </TextLink>
        ),
      })
    }

    if (serviceProvider.contact?.email) {
      items.push({
        label: translations.email,
        value: (
          <TextLink
            to={`mailto:${serviceProvider.contact.email}`}
            target="_blank"
            externalURL
          >
            {serviceProvider.contact.email}
          </TextLink>
        ),
      })
    }

    if (serviceProvider.specializations?.length > 0) {
      items.push({
        label: translations.specializations,
        value: (
          <Row gap="0.5rem" style={{ flexWrap: 'wrap' }}>
            {serviceProvider.specializations.map(({ name }, index) => (
              <Tag
                key={`specialization-${index}`}
                variant="transparent"
                size="small"
                label={name}
                style={{ alignSelf: 'start', whiteSpace: 'nowrap' }}
              />
            ))}
          </Row>
        ),
      })
    }

    if (serviceProvider.activeRegions?.length > 0) {
      items.push({
        label: translations.activeRegions,
        value: (
          <Row gap="0.5rem" style={{ flexWrap: 'wrap' }}>
            {serviceProvider.activeRegions.map((region, index) => (
              <Tag
                key={`region-${index}`}
                variant="transparent"
                size="small"
                label={region.name}
                style={{ alignSelf: 'start', whiteSpace: 'nowrap' }}
              />
            ))}
          </Row>
        ),
      })
    }

    return items
  }, [serviceProvider, translations])

  return <DetailsPanel items={detailPanelItems} />
}
